import { Partner } from 'library/ts/model/enums/partner/Partner';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { IAviaPlugin } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlugin';
import { AviaPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/AviaPlugin';

/**
 * @copyright © Paramount Streaming
 * @description DIT DataHub tracking plugin for analytics and measurement
 *
 * **Analytics Integration:**
 * Integrates DIT's DataHub service with Smart Tag's analytics system to provide
 * comprehensive video analytics and user behavior measurement across the platform.
 *
 * **Tracking Activation Logic:**
 * Uses sophisticated business logic to determine when tracking should be enabled:
 * - **DIT DataHub Availability**: Verifies DIT DataHub plugin (`dit.svc.avia`) availability in DOM
 * - **Tealium Integration (Pluto Web is the only exception)**: every other embed/brand
 *   combination still requires `utag` (Tealium) presence; on Pluto Web specifically, `dit.svc`
 *   is bootstrapped by the app independent of Tealium, so `utag` is not a valid readiness
 *   signal there (PLBCK-8717)
 * - **Smart Tag Control**: Respects Smart Tag's tracking disable flags and policies
 * - **Environment Adaptation**: Adjusts tracking behavior for different environments
 *
 * **Business Logic Considerations:**
 * - **Privacy Compliance**: Honors user tracking preferences and consent requirements
 * - **Performance Impact**: Optional plugin that doesn't block video playback if unavailable
 * - **Data Collection**: Enables business intelligence and user behavior analytics
 * - **Cross-Platform Tracking**: Consistent measurement across Desktop, Mobile, and Smart TV
 *
 * **DIT Integration:**
 * Uses DIT's DataHub namespace ('dit.svc.avia') for seamless
 * integration with DIT's analytics infrastructure while maintaining Smart Tag control.
 * On Pluto Web, the app bootstraps `dit.svc` directly (PLBCK-8717); everywhere else (CTV, and
 * P+/CBS.com Web), DIT is loaded by Tealium. Either way it's self-contained once available.
 *
 * **Business Impact:**
 * Enables data-driven decision making through comprehensive video analytics while
 * maintaining user privacy and platform performance requirements.
 */
export class DataHubPlugin extends AviaPlugin implements IAviaPlugin {
	// no NPM package used for this plugin
	package = undefined;
	// no namespaceKey needed since we are overriding the complete namespace
	// must set this to undefined otherwise it is inherited.
	namespaceKey = undefined;

	override get namespaceComplete(): string {
		return 'dit.svc.avia';
	}

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'DataHubPlugin'
			}
		);
	}

	/**
	 * @description Determines if tracking should be enabled based on environment and business logic
	 *
	 * **Tracking Activation Requirements:**
	 * Uses comprehensive checks to ensure tracking is both technically possible and
	 * business-appropriate before enabling analytics collection.
	 *
	 * **Activation Criteria:**
	 * 1. **DIT DataHub Check**: DIT DataHub plugin (`dit.svc.avia`) must be loaded in the DOM
	 * 2. **Tealium Integration (Pluto Web is the only exception)**: every other embed/brand
	 *    combination still requires `utag` to be available; on Pluto Web specifically,
	 *    `dit.svc` is bootstrapped by the app independent of Tealium (PLBCK-8717), so `utag`
	 *    is not a valid readiness signal there
	 * 3. **Smart Tag Policy**: Smart Tag tracking disabled flag must not be set
	 *
	 * **Business Logic:**
	 * - **Privacy Compliance**: Respects user and content tracking preferences
	 * - **Technical Dependencies**: Ensures all required tracking infrastructure is available
	 * - **Performance Considerations**: Only enables when technically feasible
	 * - **Environment Adaptation**: Handles development vs production environments
	 *
	 * @returns {boolean} True if all tracking requirements are met and tracking should be enabled
	 */
	shouldBeUsed() {
		// if TAG params doesn't indicate to prefer legacy tracking over DIT DataHub tracking
		const useLegacyTrackingOverride = this?.params?.TAG?.useLegacyTracking || false;

		if ( useLegacyTrackingOverride ) {
			this.logger?.log( 'Legacy tracking is enabled, disable DIT DataHub plugin' );
			return false;
		}

		// if DIT DataHub plugin reference exists in DOM namespace
		const ditDataHubPluginExists = this.utils.Type.isDefined( this.loadedPlugin );
		// if TAG params doesn't indicate tracking should be disabled
		const trackingDisabled = this?.params?.TAG?.trackingDisabled || false;

		if ( this.#isPlutoWeb() ) {
			// Pluto Web only — dit.svc bootstraps independent of Tealium (PLBCK-8717)
			return ditDataHubPluginExists && trackingDisabled === false;
		}

		// CTV, P+, CBS.com — unchanged: utag remains a required readiness signal
		const utagExists = this.utils.Type.isDefined( window?.utag );
		return utagExists && ditDataHubPluginExists && trackingDisabled === false;
	}

	// true only for Pluto Web, where dit.svc bootstraps independent of Tealium (PLBCK-8717);
	// every other embed/brand combination still gates on utag in shouldBeUsed()
	#isPlutoWeb(): boolean {
		const isWebEmbed = this?.params?.SYSTEM?.embedType === EmbedType.DESKTOP
			|| this?.params?.SYSTEM?.embedType === EmbedType.MOBILE_WEB;

		// partner is host-supplied and may be omitted; isPTV() derives Pluto from the page host
		// instead. Neither is a superset of the other (e.g. a partner iframe embed on a
		// non-pluto.tv domain can have partner === PTV with isPTV() false), so accept either.
		const isPluto = this?.params?.CONTENT?.partner === Partner.PTV || this.isPTV();

		return isWebEmbed && isPluto;
	}
}